import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { Button, Loader, PlanBadge, Text, tokens } from '@hotjar/hotsauce';

import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { isSiteAllowed } from '@hotjar/webapp/domains/common/feature';
import { useSites } from '@hotjar/site';
import type { Site } from '@hotjar/site';
import { SCALE } from '@hotjar/webapp/domains/revenue/plan/feature-group';
import {
  getProductBySku,
  getProductSkusByPlanId,
} from '@hotjar/webapp/domains/common/product/utils';
import { useProductCatalog } from '@hotjar/webapp/domains/common/product/hooks/useProductCatalog';
import { useHelpHotjarUrl } from '@hotjar/translations';

const LEARN_MORE_URL = '/articles/1500005375602';

const InfoBox = styled.div`
  border: 1px solid ${tokens.color.ui_border};
  padding: ${tokens.spacer.x5};
`;

const TitleWrapper = styled.div`
  flex-grow: 1;
  padding-top: ${tokens.spacer.x0};
  margin-bottom: ${tokens.spacer.x4};
`;

const TextWrapper = styled(Text)`
  margin-bottom: ${tokens.spacer.x3};
`;

const StyledBadge = styled(PlanBadge)`
  margin-bottom: ${tokens.spacer.x1};
`;

const ContentWrapper = styled.div`
  display: flex;
`;

const BoxCTAs = styled.div`
  margin-top: ${tokens.spacer.x3};
  display: flex;
`;
const CtaButton = styled(Button)`
  margin-right: ${tokens.spacer.x2};
`;

export const SsoRequest = (): JSX.Element => {
  const { t } = useTranslation();
  const helpHotjarUrl = useHelpHotjarUrl();
  const {
    query: { data: sites, isLoading },
  } = useSites();
  const { click } = useTrack();

  const { data: productCatalog } = useProductCatalog();

  const isEligibleSite = useCallback(
    (site: Site): boolean => {
      const [analyticsProd, feedbackProd] = getProductSkusByPlanId(site.planId);
      const plan =
        getProductBySku(analyticsProd, productCatalog)?.tier === SCALE ||
        getProductBySku(feedbackProd, productCatalog)?.tier === SCALE;

      return isSiteAllowed('features.sso', site) || !!plan;
    },
    [productCatalog],
  );

  const hasSiteInScale = useCallback(
    () => sites && sites.some((site) => isEligibleSite(site)),
    [isEligibleSite, sites],
  );

  const ctaMap = useMemo(
    () => ({
      scale: `${helpHotjarUrl}/requests/new?ticket_form_id=1500000322461`,
      'non-scale': `${helpHotjarUrl}/requests/new?ticket_form_id=360000665794&tell_us_more=cf_sales_pricing_scale&utm_medium=Insights&utm_source=SSO`,
    }),
    [helpHotjarUrl],
  );

  const copyMap = useMemo(
    () => ({
      scale: {
        title: t('common:sso.cta-banner.scale.title'),
        content: t('common:sso.cta-banner.scale.content'),
        'secondary-subtitle': t('common:sso.cta-banner.scale.sub-title'),
        'secondary-content': t('common:sso.cta-banner.scale.secondary-content'),
        'primary-button-text': t('common:sso.cta-banner.scale.primary-button-text'),
      },
      'non-scale': {
        title: t('common:sso.cta-banner.non-scale.title'),
        content: t('common:sso.cta-banner.non-scale.content'),
        'secondary-subtitle': t('common:sso.cta-banner.non-scale.sub-title'),
        'secondary-content': t('common:sso.cta-banner.non-scale.secondary-content'),
        'primary-button-text': t('common:sso.cta-banner.non-scale.primary-button-text'),
      },
    }),
    [t],
  );

  const trackClickEvent = useCallback(
    (eventName) => {
      click(eventName, 'settings.sso', {
        state_event_was_triggered_on: 'settings.sso',
        url_event_was_triggered_on: '/sso/provider/list',
        user_has_sites_in_scale: hasSiteInScale(),
      });
    },
    [click, hasSiteInScale],
  );

  const renderCorrectCopy = useMemo(
    () =>
      (
        key:
          | 'title'
          | 'content'
          | 'secondary-subtitle'
          | 'secondary-content'
          | 'primary-button-text',
      ) =>
        copyMap[hasSiteInScale() ? 'scale' : 'non-scale'][key],
    [hasSiteInScale, copyMap],
  );

  const getCorrectLinks = useCallback(
    () => ctaMap[hasSiteInScale() ? 'scale' : 'non-scale'],
    [ctaMap, hasSiteInScale],
  );

  const handleRequestSSOClick = useCallback(() => {
    trackClickEvent('SAML SSO Lead Gen CTA');
    window.open(getCorrectLinks(), '_blank', 'noopener noreferrer');
  }, [trackClickEvent, getCorrectLinks]);

  const handleLearnMoreClick = useCallback(() => {
    trackClickEvent('SAML SSO Lead Gen Learn More');
    window.open(`${helpHotjarUrl}${LEARN_MORE_URL}`, '_blank', 'noopener noreferrer');
  }, [trackClickEvent, helpHotjarUrl]);

  if (!sites || isLoading) {
    return <Loader size="l" />;
  }

  return (
    <InfoBox data-testid="sso-request">
      <div>
        <ContentWrapper>
          <TitleWrapper>
            <StyledBadge kind="scale" />
            <Text as="h2" textType="header4">
              {renderCorrectCopy('title')}
            </Text>
          </TitleWrapper>
        </ContentWrapper>
        <div>
          <TextWrapper as="p" textType="body2">
            {renderCorrectCopy('content')}
          </TextWrapper>
          <div data-testid="sso-content">
            <Text as="span" textType="subtitle2">
              {renderCorrectCopy('secondary-subtitle')}
            </Text>
            <Text as="p" textType="body2">
              {renderCorrectCopy('secondary-content')}
            </Text>
          </div>
          <BoxCTAs>
            <CtaButton kind="primary" size="m" onClick={handleRequestSSOClick}>
              {renderCorrectCopy('primary-button-text')}
            </CtaButton>
            <CtaButton kind="secondary" size="m" onClick={handleLearnMoreClick}>
              {t('common:sso.learn-more-button')}
            </CtaButton>
          </BoxCTAs>
        </div>
      </div>
    </InfoBox>
  );
};
